import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const FAQ_KEYS = [
  { q: 'butlerTrainingSchool.faq.q1', a: 'butlerTrainingSchool.faq.a1' },
  { q: 'butlerTrainingSchool.faq.q2', a: 'butlerTrainingSchool.faq.a2' },
  { q: 'butlerTrainingSchool.faq.q3', a: 'butlerTrainingSchool.faq.a3' },
  { q: 'butlerTrainingSchool.faq.q4', a: 'butlerTrainingSchool.faq.a4' },
  { q: 'butlerTrainingSchool.faq.q5', a: 'butlerTrainingSchool.faq.a5' },
];

function splitParagraphs(text: string): string[] {
  return text.split(/\n{2,}/);
}

export default function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_KEYS.map((faq) => ({
      '@type': 'Question',
      name: t(faq.q),
      acceptedAnswer: {
        '@type': 'Answer',
        text: String(t(faq.a)).replace(/\n/g, ''),
      },
    })),
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <div className="max-w-[840px] mx-auto px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('butlerTrainingSchool.faq.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('butlerTrainingSchool.faq.title')}
          </h2>
          <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">
            {t('butlerTrainingSchool.faq.subtitleEn')}
          </p>
          <div className="gold-line mx-auto"></div>
        </div>

        <div className="space-y-3">
          {FAQ_KEYS.map((faq, idx) => (
            <div key={idx} className="border border-background-200/60 bg-background-100/40">
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="text-[16px] text-foreground-800 tracking-[0.04em] font-medium pr-4">
                  {t(faq.q)}
                </span>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-accent-500 text-sm transition-transform duration-300 ${
                      openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? 'max-h-[1200px]' : 'max-h-0'
                }`}
              >
                {splitParagraphs(String(t(faq.a))).map((paragraph, pIdx) => (
                  <p key={pIdx} className="px-5 pb-5 text-[16px] text-foreground-500 tracking-[0.04em] leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[14px] text-foreground-400 tracking-[0.04em] mt-8">
          {t('butlerTrainingSchool.faq.footer')}
        </p>
      </div>
    </section>
  );
}
